import { useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { MdDelete, MdKeyboardArrowRight } from 'react-icons/md'
import { useParams } from 'react-router-dom'

import type { GetRowIdParams, RowClickedEvent, SortChangedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IReport } from '~/types/report'

import columns from './columns'
import ReportsFilters, { initialState as filtersInitialState } from './Filters'
import PanelContent from './PanelContent'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import Panel from '~/components/Panel'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { DEFAULT_SORT, useTablePagination } from '~/hooks/useTablePagination'
import { useAppSummary } from '~/providers/AppSummary'
import { keys as reportsKeys, useProcessBatchReportMutation, useReportsByEntitiesQuery } from '~/queries/reports'
import { transformByDeviceType } from '~/utils/application'
import { handleGenericError } from '~/utils/error'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const noOp = () => null

const ENTRIES_NAME = 'reports'

const Reports = (): ReactElement => {
  /** Local state */

  const { postId } = useParams()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<IReport>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const { appDetails, appKey, deviceType } = useAppSummary()

  const [selectedReportEntityIds, setSelectedReportEntityIds] = useState<string[]>([])

  const [isRemoveModalOpen, setRemoveModalState] = useState(false)

  const [isIgnoreModalOpen, setIgnoreModalState] = useState(false)

  const [rowData, setRowData] = useState<IReport[] | null>()

  const [total, setTotal] = useState(0)

  const [selectedReportId, setSelectedReportId] = useState<string>()

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const paginationApi = useTablePagination(ENTRIES_NAME, filtersInitialState)

  const { filters, orderBy, page, search, setFilters, setOrderBy, setPage, size } = paginationApi

  const columnDefs = useMemo(() => columns, [])

  /** Queries & Mutations */

  const { data: reportsData, isLoading: isReportsLoading } = useReportsByEntitiesQuery({
    appKey,
    deviceType,
    ['order-by']: orderBy,
    page,
    size,
    ...search,
    ...filters,
  })

  const { mutateAsync: processBatchReport } = useProcessBatchReportMutation()

  /** Effects */

  useDocumentTitle('Reports')

  const handleClickOutside = useCallback(
    (event: any) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current?.contains(event.target) || panelRef.contains(event.target))) ||
        !document.getElementById('root')?.contains(event.target)
      ) {
        return
      }
      if (gridRef.current?.api && !!selectedReportId) {
        setSelectedReportId(undefined)
        gridRef.current?.api.deselectAll()
      }
    },
    [panelRef, selectedReportId]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [panelRef, handleClickOutside])

  useEffect(() => {
    if (postId && rowData) {
      const report = rowData.find(({ data }) => {
        if ('post_id' in data) {
          return data.post_id === postId
        }
        return false
      })

      if (report) setSelectedReportId(report.reported_entity_id)
    }
  }, [postId, rowData])

  useEffect(() => {
    if (reportsData) {
      setRowData(reportsData.data)
      setTotal(reportsData.total)
    }
  }, [reportsData])

  /** Handlers */

  const handleFiltersChanged = useCallback(
    (filter: string, values: string[]) => {
      setFilters((filters) => ({
        ...filters,
        [filter]: values,
      }))
      setPage(0)
    },
    [setFilters, setPage]
  )

  const handleResetFilters = useCallback(() => {
    setFilters((filters) => ({ ...filters, ...filtersInitialState }))
    setPage(0)
  }, [setFilters, setPage])

  const handleSortChange = useCallback(
    (event: SortChangedEvent) => {
      const colState = event.api.getColumnState()
      const sortState = colState.filter((s) => s.sort != null)

      setOrderBy(sortState.length ? `${sortState[0].colId} ${sortState[0].sort}` : DEFAULT_SORT)
    },
    [setOrderBy]
  )

  const handleRowClicked = useCallback((evt: RowClickedEvent) => {
    const {
      data: { reported_entity_id },
    } = evt

    setSelectedReportId(reported_entity_id)
  }, [])

  const handleSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes() || []
    const selecteNodesdIds = selectedNodes.reduce(
      (prevIds, { data }) => (data?.reported_entity_id ? [...prevIds, data.reported_entity_id] : prevIds),
      [] as string[]
    )

    setSelectedReportEntityIds(selecteNodesdIds)
  }, [])

  const handleClosePanel = () => {
    setSelectedReportId(undefined)
    gridRef.current?.api.deselectAll()
  }

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  const handleGetRowId = useCallback(({ data }: GetRowIdParams<IReport>) => data.reported_entity_id, [])

  const handleRemoveModalState = (isOpen: boolean) => () => setRemoveModalState(isOpen)

  const handleIgnoreModalState = (isOpen: boolean) => () => setIgnoreModalState(isOpen)

  const handleRemoveConfirm = async () => {
    if (selectedReportEntityIds) {
      try {
        await processBatchReport({
          appKey,
          decision: 'delete',
          deviceType,
          reportIds: selectedReportEntityIds,
        })
        await queryClient.invalidateQueries({ queryKey: reportsKeys.all })
        gridRef.current?.api.deselectAll()
        displayToast(
          `${selectedReportEntityIds.length} reported entit${
            selectedReportEntityIds.length > 1 ? 'ies were' : 'y was'
          } successfully removed`,
          'success'
        )
      } catch (e) {
        handleGenericError(e)
      } finally {
        setRemoveModalState(false)
      }
    }
  }

  const handleIgnoreConfirm = async () => {
    if (selectedReportEntityIds) {
      try {
        await processBatchReport({
          appKey,
          decision: 'no-action',
          deviceType,
          reportIds: selectedReportEntityIds,
        })
        await queryClient.invalidateQueries({ queryKey: reportsKeys.all })
        displayToast(
          `${selectedReportEntityIds.length} reported entit${
            selectedReportEntityIds.length > 1 ? 'ies were' : 'y was'
          } successfully ignored`,
          'success'
        )
        gridRef.current?.api.deselectAll()
      } catch (e) {
        handleGenericError(e)
      } finally {
        setIgnoreModalState(false)
      }
    }
  }

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <div className="view-layout">
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <ViewTitle className="layout-view-title">Moderation</ViewTitle>
      <ReportsFilters onFiltersChange={handleFiltersChanged} onResetFilters={handleResetFilters} />
      <Layout className="reports-view" withMargin>
        <div className="reports-view__content">
          <ContentLayout>
            <Section className="section">
              <ServerSideTableWrapper
                buttonComponent={
                  <div className="reports-view__content__button-container">
                    <Button disabled={selectedReportEntityIds.length === 0} onClick={handleRemoveModalState(true)}>
                      Remove selected
                    </Button>
                    <Button
                      disabled={selectedReportEntityIds.length === 0}
                      onClick={handleIgnoreModalState(true)}
                      outlined
                    >
                      Ignore selected
                    </Button>
                  </div>
                }
                className="reports-table with-min-height"
                columnDefs={columnDefs}
                currentPage={page}
                entriesName={ENTRIES_NAME}
                getRowId={handleGetRowId}
                loading={isReportsLoading}
                onRowClicked={handleRowClicked}
                onSelectionChanged={handleSelectionChanged}
                onSortChanged={handleSortChange}
                paginationApi={paginationApi}
                ref={gridRef}
                rowData={rowData}
                rowHeight={150}
                rowSelection={{
                  enableClickSelection: true,
                  headerCheckbox: true,
                  mode: 'multiRow',
                }}
                suppressScrollOnNewData
                tableRef={tableRef}
                total={total}
              />
            </Section>
          </ContentLayout>
        </div>
      </Layout>
      <Panel
        className="reports-panel"
        isOpen={!!selectedReportId}
        onClosePanel={handleClosePanel}
        panelRef={contentRefCallback}
      >
        {selectedReportId ? <PanelContent entityId={selectedReportId} /> : null}
      </Panel>
      <ModalDialog
        btnConfirm={<Button onClick={handleRemoveConfirm}>Remove</Button>}
        icon={<MdDelete color="#ca3423" size={65} />}
        isOpen={isRemoveModalOpen}
        mainText={`Do you really want to remove ${selectedReportEntityIds.length} entities reported ?`}
        onClose={handleRemoveModalState(false)}
        onConfirm={noOp}
      />
      <ModalDialog
        btnConfirm={<Button onClick={handleIgnoreConfirm}>Ignore</Button>}
        icon={<MdKeyboardArrowRight color="#06703d" size={65} />}
        isOpen={isIgnoreModalOpen}
        mainText={`Do you really want to ignore ${selectedReportEntityIds.length} entities reported ?`}
        onClose={handleIgnoreModalState(false)}
        onConfirm={noOp}
      />
    </div>
  )
}

export default Reports
